package ai.jessie.web.authentication

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.w3c.fetch.ERROR
import org.w3c.fetch.NO_STORE
import org.w3c.fetch.RequestCache
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import org.w3c.fetch.RequestRedirect
import org.w3c.fetch.SAME_ORIGIN

enum class SessionStatus {
    CHECKING,
    AUTHENTICATED,
    UNAUTHENTICATED,
    UNAVAILABLE,
}

fun interface AuthenticationSessionPort {
    suspend fun status(): SessionStatus
}

class BrowserAuthenticationSessionPort : AuthenticationSessionPort {
    override suspend fun status(): SessionStatus {
        val response =
            window
                .fetch(
                    "/api/v1/authentication/session",
                    RequestInit(
                        method = "GET",
                        credentials = RequestCredentials.SAME_ORIGIN,
                        cache = RequestCache.NO_STORE,
                        redirect = RequestRedirect.ERROR,
                    ),
                )
                .await()
        return when (response.status.toInt()) {
            HTTP_NO_CONTENT -> SessionStatus.AUTHENTICATED
            HTTP_UNAUTHORIZED -> SessionStatus.UNAUTHENTICATED
            else -> SessionStatus.UNAVAILABLE
        }
    }
}

private const val HTTP_NO_CONTENT = 204
private const val HTTP_UNAUTHORIZED = 401

val LocalAuthenticationSessionPort =
    staticCompositionLocalOf<AuthenticationSessionPort> {
        error("Authentication session port must be supplied by the application")
    }

data class AuthenticationSessionUiState(val status: SessionStatus = SessionStatus.CHECKING)

data class AuthenticationSessionActions(val refresh: () -> Unit)

class AuthenticationSessionState(
    private val port: AuthenticationSessionPort,
    private val scope: CoroutineScope,
) {
    var state by mutableStateOf(AuthenticationSessionUiState())
        private set

    val actions = AuthenticationSessionActions(::refresh)

    fun refresh() {
        state = AuthenticationSessionUiState()
        scope.launch {
            state =
                AuthenticationSessionUiState(
                    try {
                        port.status()
                    } catch (cancelled: CancellationException) {
                        throw cancelled
                    } catch (_: Exception) {
                        SessionStatus.UNAVAILABLE
                    }
                )
        }
    }
}
